export type YandexCoordinates = [number, number];

export type YandexMapInstance = {
  destroy: () => void;
  setCenter: (coordinates: YandexCoordinates, zoom?: number, options?: Record<string, unknown>) => void;
  setBounds?: (bounds: [YandexCoordinates, YandexCoordinates], options?: Record<string, unknown>) => void;
  getBounds?: () => [YandexCoordinates, YandexCoordinates];
  getZoom?: () => number;
  events: {
    add: (eventName: string, handler: (event: YandexMapEvent) => void) => void;
  };
  geoObjects: {
    add: (object: unknown) => void;
    removeAll?: () => void;
  };
};

export type YandexPlacemarkInstance = {
  events: {
    add: (eventName: string, handler: () => void) => void;
  };
  geometry: {
    getCoordinates: () => YandexCoordinates;
    setCoordinates: (coordinates: YandexCoordinates) => void;
  };
};

export type YandexMapEvent = {
  get: (key: string) => unknown;
};

export type YandexGeoObject = {
  geometry?: {
    getCoordinates: () => YandexCoordinates;
  };
  getAddressLine?: () => string;
  getAdministrativeAreas?: () => string[];
  getCountry?: () => string | null;
  getLocalities?: () => string[];
  getPremiseNumber?: () => string | null;
  getThoroughfare?: () => string | null;
  properties?: {
    get: (key: string, defaultValue?: unknown) => unknown;
  };
};

export type YandexGeocodeResult = {
  geoObjects: {
    get: (index: number) => YandexGeoObject | null;
    getLength: () => number;
  };
};

export type YandexMapsApi = {
  ready: (callback: () => void) => void;
  geocode: (
    request: string | YandexCoordinates,
    options?: Record<string, unknown>,
  ) => Promise<YandexGeocodeResult>;
  Map: new (
    container: HTMLElement,
    state: Record<string, unknown>,
    options?: Record<string, unknown>,
  ) => YandexMapInstance;
  Placemark: new (
    coordinates: YandexCoordinates,
    properties?: Record<string, unknown>,
    options?: Record<string, unknown>,
  ) => YandexPlacemarkInstance;
};

declare global {
  interface Window {
    ymaps?: YandexMapsApi;
  }
}

export const YANDEX_MAPS_API_KEY = "bb5b5951-44d3-4057-a7fa-414222302dba";

const YANDEX_MAPS_SCRIPT_ID = "yandex-maps-api";
let yandexMapsPromise: Promise<YandexMapsApi> | null = null;

export const loadYandexMaps = (): Promise<YandexMapsApi> => {
  if (window.ymaps) {
    return new Promise((resolve) => window.ymaps?.ready(() => resolve(window.ymaps as YandexMapsApi)));
  }

  if (yandexMapsPromise) return yandexMapsPromise;

  yandexMapsPromise = new Promise((resolve, reject) => {
    const resolveReady = () => {
      if (!window.ymaps) {
        reject(new Error("Yandex Maps API is unavailable"));
        return;
      }
      window.ymaps.ready(() => resolve(window.ymaps as YandexMapsApi));
    };

    const existingScript = document.getElementById(YANDEX_MAPS_SCRIPT_ID) as HTMLScriptElement | null;
    if (existingScript) {
      existingScript.addEventListener("load", resolveReady, { once: true });
      existingScript.addEventListener("error", () => reject(new Error("Failed to load Yandex Maps API")), {
        once: true,
      });
      return;
    }

    const script = document.createElement("script");
    script.id = YANDEX_MAPS_SCRIPT_ID;
    script.src = `https://api-maps.yandex.ru/2.1/?apikey=${YANDEX_MAPS_API_KEY}&lang=ru_RU`;
    script.async = true;
    script.onload = resolveReady;
    script.onerror = () => reject(new Error("Failed to load Yandex Maps API"));
    document.head.appendChild(script);
  });

  return yandexMapsPromise;
};

export const parseCoordinate = (value: unknown): number | null => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};
